import { AccessList } from "./AccessList.ts";
import { PartyAgreements } from "./Agreements.ts";
import {
  CostSummary,
  OutcomeSummary,
  OutcomesForParty,
  PartyTotals,
} from "./Analytics.ts";
import { ApportionmentItem } from "./apportionmentTypes.ts";
import { CostType, Stage } from "./CaseConfiguration.ts";
import { Budget, LockedCosts } from "./CostModel.ts";
import type { ReserveHeads } from "./reserve.ts";
import type { Closing, Judgment } from "./judgment.ts";
import type { IsoDate, Timeline } from "./timeline.ts";
import { SettlementBasis } from "./settlementCaps.ts";
import type { SettlementAdjusted } from "./settlementAdjusted.ts";
import type { SettlementChances } from "./settlementProspects.ts";
import { WeightedValue, getValue } from "./WeightedValue.ts";

export interface NamedEntity {
  readonly id: string;
  name: string;
}

export interface NamedValue extends NamedEntity {
  values: WeightedValue[];
}

export interface CostApportionment {
  generic: number;
  perParty: Apportionment;
}

export interface Party extends NamedEntity {
  client?: boolean;
  assets?: NamedValue[] | "unlimited";
  recoveries?: CostRecovery[];
  apportionment?: CostApportionment;
  /**
   * The rate of the VAT the party pays on its costs because it cannot reclaim
   * it, 0.2 for 20%. Absent when the party reclaims its VAT: VAT is then
   * neutral for it and left out of its figures (see vat.ts).
   */
  vatRate?: number;
}

/**
 * A loss a claimant claims. It may be claimed in several of its claims, at a
 * different amount in each, and is awarded once: in an outcome, the highest
 * amount of the claims won (see Quantum).
 */
export interface Damage {
  readonly id: string;
  readonly claimantId: string;
  name: string;
}

/** What a claim (or an issue) claims of a damage of its claimant. */
export interface Quantum {
  // the damage claimed (see Damage), at most once per claim
  damageId: string;
  // absolute amount of currency
  value: number;
  // in %, weighted average
  awardedShares: WeightedValue[];
  // in % per defendant
  liabilityShares?: { [defendantId: string]: number };
}

export function getAwardedValue({ awardedShares, value }: Quantum): number {
  return awardedShares && awardedShares.length > 0
    ? getValue(awardedShares).mul(value).toNumber()
    : value;
}

export enum IssueRelation {
  And,
  Or,
  Xor,
}

export enum ClaimTypes {
  ProfessionalNegligence = "Professional negligence",
  BreachOfContract = "Breach of contract",
  Fraud = "Fraud",
  PersonalInjury = "Personal injury",
  Defamation = "Defamation",
  Property = "Property",
  Commercial = "Commercial",
  MedicalNegligence = "Medical negligence",
  ContentiousTrustAndProbate = "Contentious trust and probate",
  Employment = "Employment",
  Insolvency = "Insolvency",
  Competition = "Competition",
  Insurance = "Insurance",
  IntellectualProperty = "Intellectual property",
  GroupAction = "Group / class action",
  InternationalArbitration = "International arbitration",
  BankingAndFinancialServices = "Banking & financial services",
  Other = "Other",
}

/**
 * Win chances per defendant, which also say who an issue runs against: a key
 * means the defendant is involved, `null` that no chance has been entered for
 * them yet (which blocks the calculation), and no key that they are not
 * involved.
 */
export type WinChances = { [defendantId: string]: number | null };

export interface Issue {
  readonly id: string;
  title: string;
  relation?: IssueRelation;
  quantums?: Quantum[];
  defendants?: NamedEntity[];
  // if truthy, winChance applies
  generic?: boolean;
  genericWinChance?: number;
  perDefendantWinChance?: WinChances;
  subIssues?: Issue[];
}

export interface Claim extends Issue {
  claimant: NamedEntity;
  counterToClaim?: string;
  setOff?: boolean;
  issues?: Issue[];
  type?: string;
}

export type Apportionment = { [party: string]: number };

export interface Cost {
  title: string;
  value: number;
  incurred?: boolean;
  apportionment?: Apportionment[];
}

export interface CostStructure {
  // expressed in %, margin=(1-indirectCosts-directCosts)
  clientFeeStructure?: FeeStructure;
}

export interface FeeStructure {
  // 0-1 percentage of fixed costs
  indirectCostsPercentage: number;
  // 0-1 percentage of personnel costs
  directCostsPercentage: number;
}

export interface Revenue {
  incurred: {
    indirectCosts: number;
    directCosts: number;
    margin: number;
  };
  estimated: Revenue["incurred"];
}

export interface RevenueResults {
  perClient: { [partyId: string]: Revenue };
  total: Revenue;
}

// Per-stage law-firm revenue breakdown for the Revenue analytics tab. Computed
// server-side in `calculateResults` (it aggregates probability-weighted CFA
// fees from every outcome's `financialOutcomes`, which is otherwise stripped
// from stored results) and persisted as `Results.revenueData` so the client
// reads the small aggregate instead of re-deriving from per-outcome details.
export type StageRevenue = {
  stage: Stage;
  indirectCosts: number;
  directCosts: number;
  margin: number;
  cfaSuccessFee: number;
  cfaNoWinFee: number;
};

export type RevenueData = {
  incurredRevenue: StageRevenue[];
  estimatedRevenue: StageRevenue[];
  incurredTotal: number;
  estimatedTotal: number;
  cfaTotal: number;
  cfaExpenses: number; // positive total of CFA costs for summary
  total: number;
};

export enum OfferStatus {
  Draft,
  Offered,
  Rejected,
  Accepted,
}

/** A side's costs of a settlement offer, by cost type. */
export type SettlementCosts = { [type in CostType]?: number };

export interface SettlementOffer {
  id: string;
  // party proposing offer (could be receiving or paying)
  offerorId: string;
  // party receiving offer (not necessarily the money, could be payer)
  offereeId: string;
  payment: { amount: number; offereePays: boolean };
  stage: Stage;
  status: OfferStatus;
  // not a real offer: a settlement a world of the settlement-adjusted results
  // assumes (see docs/SettlementProspects.md); never stored
  assumed?: boolean;
  // the day an accepted offer was accepted, as the user entered it on
  // accepting; absent on offers accepted before it was asked for
  acceptedOn?: IsoDate;
  costs: {
    // what the offer costs each side: spent at its stage, whatever its
    // status, on top of the side's costs on the Costs page, and towards the
    // other side alone
    settlement: {
      offeror: SettlementCosts;
      offeree: SettlementCosts;
    };
  };
  settledClaims: string[];
  // the damages the offer settles (ids of Damage)
  settledQuantums: string[];
  apportionment: ApportionmentItem[];
  // Impact snapshot for a TERMINAL offer (Rejected/Accepted), frozen at the
  // moment of the decision so it records the situation as it then was. The
  // recorded impact must NOT track the evolving case — otherwise a past
  // decision's baseline (and a settled deal's recorded effect) would drift as
  // claims resolve, costs accrue and EVs shift. Draft and Offered offers stay
  // mutable (an offer can be withdrawn), so they never store this and compute
  // live. Server-managed; absent on offers last decided before this field
  // existed.
  frozenImpact?: { offeror: OfferImpact; offeree: OfferImpact };
  // What an ACCEPTED offer covers of the receiving party's spent costs, fixed
  // when it is accepted and kept like its frozenImpact: the calculation takes
  // it as stored, and those costs stay incurred. Server-managed.
  covered?: CoveredCost[];
}

/**
 * What an accepted settlement covers of the receiving party's spent costs of
 * one stage and cost type, as the Costs page has them, without VAT: of its
 * settlement costs, of its costs towards the payer, of its generic costs.
 */
export interface CoveredCost {
  stage: Stage;
  type: CostType;
  ofSettlement?: number;
  towardsPayer?: number;
  generic?: number;
}

export interface CostRecovery {
  readonly id: string;
  type: CostType;
  percentage: number;
}

export interface Evs {
  own: ValueOfStage[];
  vsOthers: { [partyId: string]: ValueOfStage[] };
}

export interface ValueOfStage {
  stage: Stage;
  value: number;
}

export interface PartyDescriptor {
  name: string;
  isClient?: boolean;
  assetLimit: number | "unlimited";
  opponents: string[];
}

export interface PartyDescriptors {
  [partyId: string]: PartyDescriptor;
}

export interface Opponents {
  [partyId: string]: { [opponentId: string]: string[] };
}

// summary of settlement offer impact on case
export interface SettlementImpact {
  offerId: string;
  offeror: OfferImpact;
  offeree: OfferImpact;
}

export interface OfferImpact {
  // EV values at stage of offer if the offer is accepted. Absent when the two
  // parties have no settlement range at that stage — never zero-filled, which
  // would read as a real range centred on 0.
  evAtStageOfOffer?: {
    readonly ofParty: number;
    readonly mid: number;
    readonly ofOpponent: number;
  };
  // the party's whole-case outcome stats in the no-settlement world (this offer
  // REJECTED), reconstructed per offer. Its `netResult.average` is the scalar
  // no-settlement EV (read via baselineNet); the distribution also feeds the risk
  // chart, which can render even after acceptance when the case baseline already
  // bakes in the settlement. Optional: older persisted impacts predate this field
  // and gain it on recompute (auto-healed by the afterFind migration).
  baselineStats?: NetResultStats;
  // the party's whole-case outcome stats if this offer is accepted; compared in
  // the UI against the case baseline (getPartyStats). Optional: older persisted
  // settlementImpacts predate this field and only gain it on recompute.
  settledStats?: NetResultStats;
  // the party's position against each opponent it still litigates after this
  // offer — no-settlement (baseline) vs settled — to surface the cross-effect on
  // remaining disputes. Optional for the same persistence reason.
  remaining?: RemainingOpponentImpact[];
}

// A party's settlement range vs one still-litigated opponent, at the offer
// stage, in the no-settlement (baseline) vs settled world.
export interface RemainingOpponentImpact {
  readonly opponentId: string;
  readonly name: string;
  readonly baseline: { partyEv: number; mid: number; opponentEv: number };
  readonly settled: { partyEv: number; mid: number; opponentEv: number };
}

// Subset of CaseStats describing the net-result distribution of a party's
// outcomes. Shared by partyStats (baseline) and the per-offer settled world.
export type NetResultStats = Pick<
  CaseStats,
  | "netResult"
  | "probabilityOfNegativeJudgement"
  | "probabilityOfNegativeNetResult"
> & {
  // Firm & cost economics of the world this snapshot describes, reusing the
  // engine's existing shapes (no bespoke type). Optional: absent on the
  // per-offer baseline/settled stats (which don't populate them) and on
  // snapshots computed before this field existed.
  // - lawFirm: the probability-weighted law-firm result of the world, straight
  //   from OutcomesForParty.average.lawFirm.
  // - costsByType: the client party's cost by CostType, from the world's
  //   ledger totals (PartyTotals.costs).
  lawFirm?: OutcomesForParty["average"]["lawFirm"];
  costsByType?: PartyTotals["costs"];
  // the VAT the client party pays on top of `costsByType` (negative, like
  // them): absent when it pays none
  vat?: number;
  // probability of the worst net result: set on the settled-vs-litigated
  // snapshot, absent elsewhere and on snapshots computed before it existed
  worstProbability?: number;
};

export type CaseStats = {
  quantumsClaimed: {
    // how many?
    amount: number;
    // total monetary value
    totalValue: number;
  };
  quantumsClaimedAgainst: CaseStats["quantumsClaimed"];
  probabilityOfNegativeJudgement: number;
  probabilityOfNegativeNetResult: number;
  netResult: {
    best: number;
    average: number;
    worst: number;
  };
  ev: number;
  settlements: {
    accepted?: number;
    settledClaims?: number;
    settledOpponents?: number;
    // how much has the party received or will receive through settlements
    paymentsToParty?: number;
    // how much has the party paid or will pay to other parties in settlements
    paymentsByParty?: number;
  };
  costs: {
    disbursements: {
      incurred: number;
      estimated: number;
    };
    lawFirmFees: CaseStats["costs"]["disbursements"];
  };
};
export type PartyStats = {
  partyId: string;
  stats: CaseStats;
};

/**
 * What the calculation makes of a claim, an issue or a group of issues: the
 * chance it is won against each defendant still litigating it — a defendant
 * who settled has none — against all of them and against at least one, summed
 * over the case's outcomes.
 */
export type IssueWinChances = {
  againstEach: { [defendantId: string]: number };
  againstAll: number;
  againstAny: number;
};

// Full, in-memory computed results — what the engine produces and tests read.
// The persisted form is {@link StoredResults} (outcome details stripped).
export interface Results {
  partyDescriptors: PartyDescriptors;
  opponents: Opponents;
  evs: { [partyId: string]: Evs };
  costSummary: { [partyId: string]: CostSummary };
  outcomes: OutcomesForParty[];
  settlementData: SettlementDataOfParty[];
  settlementImpacts?: SettlementImpact[];
  bargainingData?: BargainingData[];
  partyStats?: PartyStats[];
  revenue?: RevenueResults;
  // Per-stage law-firm revenue breakdown for the Revenue analytics tab,
  // precomputed server-side so the client need not read per-outcome
  // financialOutcomes (stripped from stored results). Absent when the case has
  // no client fee structure.
  revenueData?: RevenueData;
  // per claim, issue and group of issues, keyed by its id as the case stores
  // it: what the claims page shows. Absent on gated results.
  issueWinChances?: { [issueId: string]: IssueWinChances };
  // per party, what its spent costs offer the settlements in which it
  // receives a sum, and what they used: the caps of an offer's apportionment
  settlementBases?: { [partyId: string]: SettlementBasis };
  // per accepted offer, what it covers, to be stored with the offer: never
  // stored with the results (see stripOutcomeDetails)
  settlementCoverage?: { [offerId: string]: CoveredCost[] };
  // per party, what accepted settlements locked of its costs
  lockedCosts?: { [partyId: string]: LockedCosts };
}

/**
 * Persisted form of {@link Results} held by `Case.results`: the heavy per-outcome
 * {@link OutcomeDetails} (`ledger`/`financialOutcomes`/`stageResults`) are stripped
 * before storage (they overflow the 16 MB BSON limit) and refetched on demand via the
 * `getOutcomeDetails` cloud function; only the light {@link OutcomeSummary} of
 * each outcome remains. A schema `version` is stamped for the lazy-migration trigger.
 */
export type StoredResults = Omit<Results, "outcomes"> & {
  /**
   * Schema marker for the lazy-migration trigger (afterFind → resave). Bumped
   * whenever the persisted shape changes. Absent/older → recompute once on read
   * to self-heal. See {@link RESULTS_VERSION}.
   */
  version?: number;
  outcomes: OutcomesForParty<OutcomeSummary>[];
};

/**
 * Current persisted `results` schema version. Written by `beforeSave`; the
 * `afterFind` lazy-migration trigger recomputes any case whose stored
 * `results.version` is missing or lower, so legacy blobs self-heal to the
 * current (details-stripped) shape on first read. Bump when the shape changes
 * — or when the same case computes to different figures, since the outcome
 * details fetched on demand always come from the current code and would
 * otherwise disagree with the stored summaries.
 *
 * 1: outcome details stripped from the stored blob. 3: settlement records and
 * realities on portfolioSummary (there was no released 2). 4: no shape change —
 * the CFA success fee is charged on the base at risk, and a minimum to
 * recover or to be awarded counts as at least 1. 5: settlementStatus on
 * portfolioSummary, so the portfolio can leave a fully settled case out of
 * its win chance and EV stats. 6: no shape change — a generic issue or claim
 * is decided once for all defendants, and a decision shared by several claims
 * counts once in an outcome's probability. 7: issueWinChances, the chances
 * the claims page shows, now computed from the outcomes. 8: the law firm's
 * expenses and net result are unknown where its fee structure is — for any
 * firm but a client's, and for a client's until the structure is entered —
 * and `revenue` is absent without a fee structure. 9: settlement costs are
 * typed, and a settlement covers costs once: its "Settlement costs" take all
 * of the receiving party's settlement costs towards the payer spent by then,
 * what it covers is used up for later settlements and for recovery at trial,
 * and the damage of a claim it settles keeps its cap. 10: a party's costs are
 * items with a part towards each opponent and a generic part, incurred and
 * estimated amounts each, hourly costs among them (the first read of a case
 * saves it so); once a party has settled with an opponent, its estimates
 * towards it are not spent, and once with all of them, none of its estimates;
 * what an accepted settlement covers is stored with it, as the Costs page has
 * it; a trigger no settlement gives a value is evaluated at the party's last
 * settlement; `lockedCosts`, what accepted settlements lock of each party's
 * costs. 11: with what accepted settlements lock of a party, the offer with
 * which it left the case (`exit`). 12: each outcome's `reserve`, its cost to
 * the party by the heads of an insurer's reserve, and per party
 * `reservePaid`, what of them is paid whatever the outcome. 13: the client
 * parties' reserves on portfolioSummary (`clientReserves`). 14: no shape
 * change of the results: a case from before the timeline gains one, the
 * stages before its clients' costs closed without a date, and its current
 * stage is the timeline's open one. 15: no shape change of the results: the
 * case gains `settlementAdjusted`, its portfolioSummary its own, and the open
 * offers move to the current stage (docs/SettlementProspects.md). 16: no shape
 * change of the results: a case settled in every claim, in Trial or before
 * it, ends there (docs/ClosingCases.md).
 */
export const RESULTS_VERSION = 16;

// configuration allows to modify main case parameters and apply them (e.g. to simulate bargaining)
export interface Configuration {
  id: string;
  timestamp: number;
  winChances: {
    [issueId: string]: {
      generic: boolean;
      genericWinChance: number;
      perDefendantWinChance: {
        [defendantId: string]: number;
      };
    };
  };
  assets: { [defendantId: string]: number | "unlimited" };
  costs: { [defendantId: string]: number | undefined };
}

export type WinChanceMutation = Variants["sensitivity"]["winChance"][0];

export interface Variants {
  bargaining?: Configuration;
  sensitivity: {
    winChance: {
      // which issue/claim
      issueId: string;
      issueLabel: string;
      // how much change
      delta: number;
      // win chance v. which defendant or, if not set, generic
      defendantId?: string;
      // results for -delta
      minusVariantId?: string;
      // results for +delta
      plusVariantId?: string;
    }[];
  };
}

/**
 * One accepted settlement, projected to a client party's perspective and frozen
 * at decision time (read off {@link SettlementOffer.frozenImpact}). Precomputed
 * into {@link PortfolioSummary} because `getPortfolioStats` deliberately does not
 * load the heavy `settlements` field — the records must already be on the summary.
 * Feeds the settled-case count and the settlement-quality chart.
 */
export interface SettlementRecord {
  caseId: string;
  caseName: string;
  // the client party this settlement is measured for
  partyId: string;
  // the other side of the offer; absent on a record stored before it was kept
  opponentId?: string;
  // the opponent's name, stamped by the reader from the case's parties like
  // `caseName`; absent with `opponentId`
  opponentName?: string;
  // settled value to the client party: positive = client receives, negative = client pays
  settledValue: number;
  // rational midpoint of the settlement range at decision time (party
  // perspective); absent when the offer had no settlement range at its stage
  mid?: number;
  // settlement-range endpoints at decision time (party perspective): the
  // quality chart places the settled value between them. Absent with `mid`.
  partyEv?: number;
  opponentEv?: number;
}

/**
 * What {@link PortfolioSummary} stores per accepted offer: a
 * {@link SettlementRecord} without the case identity. A record cannot carry it
 * reliably — a case created with settlements (an import) has no id when its
 * summary is first computed — so the readers stamp `caseId`/`caseName` from the
 * case they found the record on, and `opponentName` from its parties.
 */
export type StoredSettlementRecord = Omit<
  SettlementRecord,
  "caseId" | "caseName" | "opponentName"
>;

export type PortfolioSummary = {
  partyStats: PartyStats[];
  revenue?: RevenueResults;
  clientOutcomes: {
    partyId: string;
    samples: { probability: number; value: number }[];
  }[];
  // the firm's net result per client: unknown without a fee structure (see
  // FirmCosts), and the case then adds nothing to the profit figures
  lawFirmNetResults: ({ partyId: string } & (
    { costs: "known"; netResult: number } | { costs: "unknown" }
  ))[];
  // one entry per accepted settlement offer, from the client party's
  // perspective, without case identity (see StoredSettlementRecord); absent on
  // cases saved before this field existed (they self-heal on the next
  // recompute — see RESULTS_VERSION).
  settlementRecords?: StoredSettlementRecord[];
  // Case-level "settling vs not settling" snapshot: the case as it resolved
  // (`actual`, its settlements applied) vs the same case with NO settlements
  // (`litigated`). Each is a whole, internally-consistent computation — settled
  // claims/quantum apportionment interact and are order-dependent, so this is
  // NOT derivable by composing per-offer records. Present on any case with an
  // accepted settlement, whoever signed it: a settlement between other parties
  // changes the client's world too. Only settlementRecords are client-scoped,
  // so settledValue is zero on a case where only the opponents settled.
  settlementRealities?: SettlementRealities;
  // Case-level settlement completeness (see computeSettlementStatus), stored
  // because the portfolio reader never reloads `settlements` (see
  // getPortfolioStats). Absent on cases saved before this field existed; they
  // self-heal on the next recompute — see RESULTS_VERSION.
  settlementStatus?: "none" | "partial" | "full";
  // per client party, its reserve as its outcomes indicate it (see
  // shared/reserve.ts): the heads in all and paid, and the ultimate cost of
  // each outcome with its chance. Absent on cases saved before it.
  // the same with the settlements the user expects, where it did; absent:
  // as litigated (see settlementAdjusted.ts)
  settlementAdjusted?: Pick<
    PortfolioSummary,
    "clientOutcomes" | "lawFirmNetResults" | "partyStats"
  >;
  clientReserves?: {
    partyId: string;
    total: ReserveHeads;
    paid: ReserveHeads;
    samples: { probability: number; value: number }[];
  }[];
};

// See PortfolioSummary.settlementRealities. Client Δ, firm Δ (margin) and
// avoided-cost-by-type are all derived from the two enriched worlds; whether
// settling helped the firm is `actual.lawFirm.netResult > litigated.lawFirm.netResult`.
export interface SettlementRealities {
  // total signed settlement value to the client across the case (+ received)
  settledValue: number;
  // Σ over the case's accepted client-party offers with a known midpoint of
  // (settled value − rational midpoint at decision time); positive = settled
  // above the midpoint overall. Absent when no offer has a midpoint.
  midpointDeviation?: number;
  // actual worst-case net − litigated worst-case net; positive = settling
  // removed adverse exposure
  exposureRemoved: number;
  // the case as it resolved, with its settlements applied
  actual: NetResultStats;
  // the same case with no settlements at all
  litigated: NetResultStats;
}

/**
 * Who saved a case, resolved to a display name at save time by the server's
 * beforeSave hook. Absent on cases saved before attribution was recorded, so
 * every reader must treat it as optional.
 */
export interface CaseUserRef {
  id: string;
  name: string;
}

/**
 * A reserve an insurer booked for a party in its own books, as the user
 * enters it: CaseSolv keeps it to set against the indicated reserve, and posts
 * it nowhere. Who booked it the server stamps.
 */
export interface BookedReserve {
  readonly id: string;
  partyId: string;
  amount: number;
  bookedOn: IsoDate;
  reason?: string;
  by?: CaseUserRef;
  // brought in with an exported case, with who booked it there
  imported?: boolean;
}

/**
 * What a claim is to the insurer that models it: a third party's claim
 * against the insured it stands behind, or its own dispute with a
 * policyholder under the policy. Only an insurer sets it.
 */
export type ClaimKind = "thirdPartyLiability" | "policyholderDispute";

export interface Case extends NamedEntity {
  createdAt?: string;
  updatedAt?: string;
  createdBy?: CaseUserRef;
  updatedBy?: CaseUserRef;
  /**
   * Who may read and write the case. Set when the case is created and enforced
   * by Parse Server; the client reads it to tell a case its user may only read.
   */
  accessList?: AccessList;
  parties?: Party[];
  // absent: a third party's claim (see ClaimKind)
  claimKind?: ClaimKind;
  // the dates and stage durations the user entered (see Timeline)
  timeline?: Timeline;
  // the reserves an insurer booked, in the order they were booked
  bookedReserves?: BookedReserve[];
  // what the court decided, and how the case ended (see judgmentOf, closingOf)
  judgment?: Judgment;
  closing?: Closing;
  claims?: Claim[];
  // the damages the claims claim, each of one claimant (see Damage)
  damages?: Damage[];
  budgets?: Budget[];
  costStructure?: CostStructure;
  settlements?: SettlementOffer[];
  // what the user expects of settlements, pair by pair
  settlementChances?: SettlementChances[];
  agreements?: PartyAgreements[];
  results?: StoredResults;
  // the results with those settlements: beside `results`, the litigation path
  settlementAdjusted?: SettlementAdjusted;
  portfolioSummary?: PortfolioSummary;
  variants?: Variants;
  variantResults?: { [variantId: string]: Results };
}

/**
 * A case's editable parameters — the full {@link Case} stripped of everything the
 * server manages or computes. This is what export/import round-trips; the server's
 * beforeSave hook recomputes results/variants from it on save.
 */
export type CaseParameters = Omit<
  Case,
  | "createdAt"
  | "updatedAt"
  | "createdBy"
  | "updatedBy"
  | "accessList"
  | "results"
  | "settlementAdjusted"
  | "portfolioSummary"
  | "variants"
  | "variantResults"
>;

// settlement types
export interface SettlementRange {
  readonly partyEv: number;
  readonly mid: number;
  readonly opponentEv: number;
}
export interface SettlementRangeOfStage {
  stage: Stage;
  range: SettlementRange;
  settlement?: {
    amount: number;
    effect: SettlementRange;
  };
}
export interface SettlementRanges {
  partyId: string;
  opponentId: string;
  perStage: SettlementRangeOfStage[];
}
export interface SettlementData extends SettlementRanges {
  readonly bestEnforcement?: number;
  readonly worstEnforcement?: number;
  readonly netResult: number;
}

export interface SettlementDataOfParty {
  readonly partyId: string;
  readonly data: SettlementData[];
}

export interface BargainingData {
  partyId: string;
  opponents: {
    [opponentId: string]: {
      baseline: SettlementRanges;
      opponentRanges: SettlementRanges;
    };
  };
}
